/* eslint-disable no-console */
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { HubConnection, HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr';
import { BehaviorSubject, EMPTY, from, Observable } from 'rxjs';
import { first, switchMap } from 'rxjs/operators';

import { AuthenticationService } from 'app/core/authentication';
import { UrlService } from 'app/core/http/url.service';
import { ConnectionState } from 'app/shared/enums/connection-state.enum';
import { RTCEvent } from 'app/shared/enums/rtc-event.enum';

import { MessageBusService } from './message-bus-service.interface';
import { RealTimeEvent } from './real-time-event.model';

import { environment } from 'env/environment';

@Injectable({
  providedIn: 'root'
})
export class RTCSignalRStrategyService implements MessageBusService {
  private eventChannels: Map<string, BehaviorSubject<RealTimeEvent>> = new Map<string, BehaviorSubject<RealTimeEvent>>();
  private eventChannelsObservables: Map<string, Observable<any>> = new Map<string, Observable<any>>();

  private hubConnection: HubConnection | undefined;
  private instanceId: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  public instanceId$ = this.instanceId.asObservable();

  private connectionState: BehaviorSubject<ConnectionState> =
    new BehaviorSubject<ConnectionState>(ConnectionState.Disconnected);

  public connectionState$ = this.connectionState.asObservable();

  public constructor(private readonly authService: AuthenticationService,
    private readonly urlService: UrlService,
    private readonly httpClient: HttpClient) { }

  private defaultRTEvent: RealTimeEvent = {
    category: null,
    id: null,
    instanceId: null,
    messageObject: {},
    name: '',
    userId: null,
    validUntil: null
  };

  public disconnect(channel: string): void {
    this.eventChannels.delete(channel);
    this.eventChannelsObservables.delete(channel);
  }

  public connect(channel: string, action = '', reconnectObservable: Observable<any> | null): Observable<RealTimeEvent> {
    this.eventChannels.set(channel, new BehaviorSubject<RealTimeEvent>(this.defaultRTEvent));

    if (reconnectObservable) {
      this.eventChannelsObservables.set(channel, reconnectObservable);
    }

    if (!this.hubConnection || this.hubConnection.state === HubConnectionState.Disconnected) {
      return this.authService.getAccessToken()
        .pipe(
          first(),
          switchMap(accessToken => this.initSignalR(accessToken)
            .pipe(switchMap(() => {
              if (this.hubConnection) {
                this.hubConnection.send(RTCEvent.Subscribe, channel, action);
              }

              return (this.eventChannels.get(channel) as BehaviorSubject<RealTimeEvent>).asObservable();
            }))
          )
        );
    }

    this.hubConnection.send(RTCEvent.Subscribe, channel, action);

    return (this.eventChannels.get(channel) as BehaviorSubject<RealTimeEvent>).asObservable();
  }

  private sendText(channel: string, text: string, instanceId: string): Observable<void> {
    return this.httpClient.post<void>(
      this.urlService.buildServerApiUrl('generic/sendtext'), {
      channel, instanceId, text
    }
    );
  }

  public send(message: RealTimeEvent, channel: string): Observable<void> {
    if (!this.hubConnection) {
      return this.authService.getAccessToken()
        .pipe(
          first(),
          switchMap(accessToken => this.initSignalR(accessToken)
            .pipe(switchMap(() => {
              if (this.hubConnection) {
                return this.sendText(channel, (message.messageObject as any).text, this.instanceId.value as string);
              }

              return EMPTY;
            }))
          )
        );
    }

    return this.sendText(channel, (message.messageObject as any).text, this.instanceId.value as string);
  }

  private initSignalR(accessToken: string): Observable<void> {
    this.hubConnection = new HubConnectionBuilder()
      .withUrl(this.urlService.buildServerApiUrl('generic'), {
        accessTokenFactory: () => accessToken
      })
      .withAutomaticReconnect()
      .build();
    this.hubConnection.serverTimeoutInMilliseconds = 31000;
    this.hubConnection.keepAliveIntervalInMilliseconds = 15000;

    Object.defineProperty(WebSocket, 'OPEN', { value: 1 });

    this.hubConnection.on(RTCEvent.NewEvent, (event: RealTimeEvent) => {
      if (event.name === RTCEvent.SubscriptionConfirmation) {
        this.instanceId.next(this.hubConnection?.connectionId as string);
        this.connectionState.next(ConnectionState.Connected);

        return;
      }

      const eventChannel = this.eventChannels.get(event.category as string);
      if (eventChannel) {
        eventChannel.next(event);
      } else {
        this.eventChannels.set(event.category as string, new BehaviorSubject<RealTimeEvent>(event));
      }
    });

    this.hubConnection.onclose(() => {
      this.connectionState.next(ConnectionState.Disconnected);
      setTimeout(() => this.initSignalR(accessToken), 5000);
    });

    this.hubConnection.onreconnecting(e => {
      console.info('reconnecting', e);
      this.connectionState.next(ConnectionState.Reconnecting);
    });
    this.hubConnection.onreconnected(connectionId => {
      console.info('you are back online', connectionId);
      this.connectionState.next(ConnectionState.Connected);

      // Re-subscribe to all channels so the new connection is added back to server groups
      this.eventChannels.forEach((_, channel) => {
        this.hubConnection?.send(RTCEvent.Subscribe, channel, '');
      });

      // Fetch latest state via reconnect observables for channels that have one
      this.eventChannelsObservables.forEach((observable, channel) => {
        if (observable) {
          observable.pipe(first()).subscribe(res => {
            const eventChannel = this.eventChannels.get(channel);
            const reconnectEvent: RealTimeEvent = {
              category: channel,
              id: '200',
              instanceId: this.hubConnection?.connectionId as string,
              messageObject: res,
              name: RTCEvent.GameShowUpdated,
              userId: null,
              validUntil: null
            };
            eventChannel?.next(reconnectEvent);
          });
        }
      });
    });
    const promise = this.hubConnection.start()
      .then(() => {
        if (!environment.production) {
          console.info('connected to hub');
        }
      })
      .catch(
        err => {
          if (!environment.production) {
            console.error(err.toString());
          }
        }
      );

    return from(promise);
  }
}
